<template>
  <div class="crm-page">
    <div class="crm-hero">
      <div>
        <div class="crm-eyebrow">客户管理CRM</div>
        <h2>跟进记录</h2>
        <p>按客户、员工、跟进结果和时间查看记录，支持批量删除、未接通号码重新加入拨打名单。</p>
      </div>
      <div class="crm-actions">
        <el-button type="danger" plain icon="el-icon-delete" :disabled="!selectedIds.length" @click="deleteSelectedRecords">批量删除</el-button>
        <el-button type="primary" icon="el-icon-refresh-right" :disabled="!selectedIds.length" @click="requeueSelectedRecords">重新加入拨打名单</el-button>
      </div>
    </div>

    <el-card shadow="never">
      <el-row :gutter="12">
        <el-col :span="5"><el-input v-model.trim="filters.keyword" placeholder="客户姓名 / 手机号 / 员工" clearable @keyup.enter.native="loadRecords"></el-input></el-col>
        <el-col :span="4">
          <el-select v-model="filters.result" placeholder="跟进结果" clearable style="width: 100%" @change="loadRecords">
            <el-option v-for="item in options.followResults" :key="item" :label="item" :value="item"></el-option>
          </el-select>
        </el-col>
        <el-col :span="4">
          <el-select v-model="filters.method" placeholder="跟进方式" clearable style="width: 100%" @change="loadRecords">
            <el-option label="电话" value="电话"></el-option>
            <el-option label="微信" value="微信"></el-option>
            <el-option label="面谈" value="面谈"></el-option>
          </el-select>
        </el-col>
        <el-col :span="5">
          <el-date-picker v-model="filters.range" type="daterange" value-format="yyyy-MM-dd" range-separator="至" start-placeholder="开始时间" end-placeholder="结束时间" style="width: 100%" @change="loadRecords" />
        </el-col>
        <el-col :span="6" class="filter-actions">
          <el-button icon="el-icon-search" type="primary" @click="loadRecords">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="resetFilters">重置</el-button>
        </el-col>
      </el-row>
    </el-card>

    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>跟进记录列表</span>
        <el-tag size="mini" type="info">已选 {{ selectedIds.length }} 条</el-tag>
      </div>
      <el-table :data="records" stripe border v-loading="loading" @selection-change="handleSelectionChange">
        <el-table-column type="selection" width="46" />
        <el-table-column prop="followTime" label="跟进时间" min-width="140"></el-table-column>
        <el-table-column prop="customerName" label="客户姓名" min-width="110"></el-table-column>
        <el-table-column prop="phone" label="手机号" min-width="140">
          <template slot-scope="scope"><crm-phone-field :phone="scope.row.phone || customerPhone(scope.row)" status="pending" /></template>
        </el-table-column>
        <el-table-column prop="companyName" label="公司名称" min-width="150" show-overflow-tooltip></el-table-column>
        <el-table-column prop="employeeName" label="跟进员工" min-width="100"></el-table-column>
        <el-table-column prop="result" label="跟进结果" min-width="100"></el-table-column>
        <el-table-column prop="intentionLevel" label="意向等级" min-width="100"></el-table-column>
        <el-table-column prop="remark" label="备注" min-width="180" show-overflow-tooltip></el-table-column>
        <el-table-column prop="aiLeadCount" label="AI线索数量" width="105"></el-table-column>
        <el-table-column prop="nextFollowTime" label="下次跟进" min-width="130"></el-table-column>
        <el-table-column prop="lastCallTime" label="最近拨打" min-width="130"></el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template slot-scope="scope"><el-button type="text" @click="openDetail(scope.row)">查看详情</el-button></template>
        </el-table-column>
      </el-table>
      <div class="pager">
        <el-pagination background layout="total, sizes, prev, pager, next" :total="total" :page-size.sync="pageSize" :current-page.sync="page" @current-change="loadRecords" @size-change="loadRecords" />
      </div>
    </el-card>

    <el-dialog :visible.sync="detailVisible" width="860px" title="跟进详情" :append-to-body="true">
      <div v-if="detail" class="detail-box">
        <el-descriptions title="客户基础信息" :column="2" border size="small">
          <el-descriptions-item label="客户姓名">{{ detail.customer.name || detail.follow.customerName }}</el-descriptions-item>
          <el-descriptions-item label="手机号"><crm-phone-field :phone="detail.customer.phone || detail.follow.phone" status="pending" /></el-descriptions-item>
          <el-descriptions-item label="公司名称">{{ detail.customer.companyName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="公司地址">{{ detail.customer.companyAddress || '-' }}</el-descriptions-item>
          <el-descriptions-item label="地区">{{ detail.customer.region || '-' }}</el-descriptions-item>
          <el-descriptions-item label="问题类型">{{ detail.customer.issueType || '-' }}</el-descriptions-item>
        </el-descriptions>
        <el-descriptions title="本次跟进" :column="2" border size="small">
          <el-descriptions-item label="跟进员工">{{ detail.follow.employeeName }}</el-descriptions-item>
          <el-descriptions-item label="跟进结果">{{ detail.follow.result }}</el-descriptions-item>
          <el-descriptions-item label="意向等级">{{ detail.follow.intentionLevel || '-' }}</el-descriptions-item>
          <el-descriptions-item label="下次跟进">{{ detail.follow.nextFollowTime || '未设置' }}</el-descriptions-item>
          <el-descriptions-item label="备注" :span="2">{{ detail.follow.remark || detail.follow.summary }}</el-descriptions-item>
        </el-descriptions>
        <el-tabs>
          <el-tab-pane label="历史拨打"><el-table :data="detail.callRecords" size="mini"><el-table-column prop="callStartTime" label="拨打时间" width="150" /><el-table-column prop="employeeName" label="员工" width="100" /><el-table-column prop="callStatus" label="状态" width="100" /><el-table-column prop="duration" label="时长/秒" width="100" /></el-table></el-tab-pane>
          <el-tab-pane label="历史跟进"><el-table :data="detail.followRecords" size="mini"><el-table-column prop="followTime" label="跟进时间" width="150" /><el-table-column prop="employeeName" label="员工" width="100" /><el-table-column prop="result" label="结果" width="100" /><el-table-column prop="remark" label="备注" min-width="180" show-overflow-tooltip /></el-table></el-tab-pane>
          <el-tab-pane label="AI找号结果"><el-table :data="detail.aiLeads" size="mini"><el-table-column prop="contactType" label="类型" width="100" /><el-table-column prop="contactName" label="姓名" width="100" /><el-table-column prop="phone" label="电话" width="130" /><el-table-column prop="sourceChannel" label="来源" width="120" /><el-table-column prop="confidence" label="可信度" width="80" /><el-table-column prop="status" label="状态" width="120" /><el-table-column prop="summary" label="摘要" min-width="180" show-overflow-tooltip /></el-table></el-tab-pane>
        </el-tabs>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { postRequest } from "@/utils/api";
import { CRM_OPTIONS, loadCrmState } from "./mock";
import CrmPhoneField from "./PhoneField.vue";

export default {
  name: "CrmFollowRecords",
  components: { CrmPhoneField },
  data() {
    return {
      options: CRM_OPTIONS,
      state: loadCrmState(this.$store.getters.GET_TITLE || "当前员工"),
      filters: { keyword: "", method: "", result: "", range: [] },
      page: 1,
      pageSize: 10,
      total: 0,
      loading: false,
      records: [],
      selectedRows: [],
      detailVisible: false,
      detail: null,
    };
  },
  computed: {
    selectedIds() {
      return this.selectedRows.map((item) => item.id);
    },
  },
  created() {
    this.loadRecords();
  },
  methods: {
    apiData(res) {
      return (res && res.data) || {};
    },
    customerPhone(record) {
      const customer = (this.state.customers || []).find((item) => item.id === record.customerId);
      return (record && record.phone) || (customer && customer.phone) || "";
    },
    loadRecords() {
      this.loading = true;
      const range = this.filters.range || [];
      postRequest("/crm/followList", {
        page: this.page,
        size: this.pageSize,
        keyword: this.filters.keyword,
        method: this.filters.method,
        result: this.filters.result,
        dateStart: range[0] || "",
        dateEnd: range[1] || "",
      })
        .then((res) => {
          if (res && res.code && Number(res.code) !== 200) {
            this.$message.error(res.msg || "获取跟进记录失败");
            return;
          }
          const data = this.apiData(res);
          this.records = data.list || [];
          this.total = data.total || this.records.length;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    resetFilters() {
      this.filters = { keyword: "", method: "", result: "", range: [] };
      this.page = 1;
      this.loadRecords();
    },
    handleSelectionChange(rows) {
      this.selectedRows = rows || [];
    },
    deleteSelectedRecords() {
      if (!this.selectedIds.length) return;
      this.$confirm(`确定删除选中的 ${this.selectedIds.length} 条跟进记录吗？`, "提示", { type: "warning" })
        .then(() => postRequest("/crm/deleteFollowRecords", { ids: this.selectedIds }))
        .then((res) => {
          if (res && res.code && Number(res.code) !== 200) {
            this.$message.error(res.msg || "删除失败");
            return;
          }
          this.$message.success("删除成功");
          this.loadRecords();
        })
        .catch(() => {});
    },
    requeueSelectedRecords() {
      if (!this.selectedIds.length) return;
      postRequest("/crm/requeueFollowRecords", { ids: this.selectedIds })
        .then((res) => {
          if (res && res.code && Number(res.code) !== 200) {
            this.$message.error(res.msg || "重新加入失败");
            return;
          }
          const data = this.apiData(res);
          this.$message.success(`已重新加入 ${data.created || this.selectedIds.length} 条待重拨任务`);
          this.loadRecords();
        });
    },
    openDetail(record) {
      postRequest("/crm/followRecordDetail", { id: record.id })
        .then((res) => {
          if (res && res.code && Number(res.code) !== 200) {
            this.$message.error(res.msg || "获取详情失败");
            return;
          }
          this.detail = this.apiData(res);
          this.detailVisible = true;
        });
    },
  },
};
</script>

<style scoped>
.crm-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.crm-hero {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  padding: 24px 28px;
  border-radius: 18px;
  background: #fff;
  border: 1px solid #e8edf5;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.04);
}
.crm-eyebrow {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(64, 158, 255, 0.12);
  color: #409eff;
  font-size: 12px;
  font-weight: 700;
}
.crm-hero h2 {
  margin: 12px 0 0;
  font-size: 28px;
  color: #111827;
}
.crm-hero p {
  margin: 10px 0 0;
  color: #6b7280;
  line-height: 1.7;
}
.crm-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.pager {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}
.detail-box {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.detail-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 0;
  border-bottom: 1px solid #eef2f7;
}
.detail-row span {
  color: #6b7280;
}
.detail-note {
  margin-top: 8px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f8fbff;
  border: 1px solid #dbeafe;
  line-height: 1.8;
  color: #1f2937;
}
@media (max-width: 1200px) {
  .crm-hero {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
